import theme_icons from "../../icons.js";
import { MediaUploaderGeneric } from "../../components/image-picker-generic.js";

const { __ } = wp.i18n;
const { registerBlockType } = wp.blocks;
const { RichText, InspectorControls, ColorPalette, InnerBlocks } =
  wp.blockEditor;
const { PanelBody, SelectControl, ToggleControl } = wp.components;
const { useSelect } = wp.data;
const { useEffect } = wp.element;

const calltoaction = {
  title: "Greenheart CTA",
  icon: theme_icons.greenheart,
  category: "greenheart",
  supports: {
    align: ["wide", "full"],
  },
  //attributes
  attributes: {
    title: {
      type: "string",
      default: "",
    },
    subtitle: {
      type: "string",
      default: "",
    },
    contextHeader: {
      type: "string",
      default: "",
    },
    analyticsTag: {
      type: "boolean",
      default: false,
    },
    bypassCrop: {
      type: "boolean",
      default: false,
    },
    hLevel: {
      type: "string",
      default: "h2",
    },
    bgColor: {
      type: "string",
      default: "",
    },
    textColor: {
      type: "string",
      default: "",
    },
    variant: {
      type: "string",
      default: "",
    },
    leftImg: {
      type: "object",
      default: {},
    },
    rightImg: {
      type: "object",
      default: {},
    },
  },

  edit({ clientId, name, attributes, setAttributes }) {
    const {
      title,
      subtitle,
      bgColor,
      textColor,
      hLevel,
      analyticsTag,
      bypassCrop,
      contextHeader,
      variant,
      leftImg,
      rightImg,
    } = attributes;
    const pageSlug = useSelect((select) => {
      return select("core/editor").getCurrentPost().slug;
    }, []);

    const blockSlug = name;
    const blockId = clientId;
    const param = `?intref=${pageSlug}&block=${blockSlug}&id=${blockId}`;
    useEffect(() => {
      if (analyticsTag) {
        const blockElement = document.querySelector(
          `[data-block="${clientId}"]`,
        );
        if (blockElement) {
          const links = blockElement.querySelectorAll("a");
          links.forEach((link) => {
            const href = link.getAttribute("href");
            if (href && !href.includes(param)) {
              link.setAttribute("href", `${href}${param}`);
            }
          });
        }
      }
    }, [analyticsTag, clientId, param]);
    const styleL = { backgroundColor: bgColor ? bgColor : "transparent" };
    const styleR = { color: textColor ? textColor : "inherit" };
    const blockStyle = { ...styleL, ...styleR };

    return [
      <InspectorControls>
        <PanelBody title={__("Call To Action Settings")}>
          <p>
            <strong>Call To Action Style</strong>
          </p>
          <SelectControl
            label="Select Style"
            value={variant}
            options={[
              { label: "Default", value: "" },
              { label: "Image Left & Right", value: "img-left-right" },
              { label: "Image Right", value: "img-right" },
              { label: "Image Left", value: "img-left" },
            ]}
            onChange={(newVariant) => setAttributes({ variant: newVariant })}
          />
          <p><strong>Bypass the Image Crop?</strong></p>
          <ToggleControl
            checked={bypassCrop}
            onChange={() => setAttributes({ bypassCrop: !bypassCrop })}
          />
          <p>
            <strong>Background Color</strong>
          </p>
          <ColorPalette
            value={bgColor}
            onChange={(newColor) => setAttributes({ bgColor: newColor })}
          />
          <p>
            <strong>Text Color</strong>
          </p>
          <ColorPalette
            value={textColor}
            onChange={(newColor) => setAttributes({ textColor: newColor })}
          />
          <p>
            <strong>Analytics Tag?</strong>
          </p>
          <em>Display a unique identifier on all links within this block</em>
          <ToggleControl
            checked={analyticsTag}
            onChange={() => setAttributes({ analyticsTag: !analyticsTag })}
          />
        </PanelBody>
      </InspectorControls>,
      <div className="rbt-calltoaction" style={blockStyle}>
        <div className={`layout ${variant}`}>
          <div className="left">
            {(variant === "img-left-right" || variant === "img-left") && (
              <MediaUploaderGeneric
                photo={leftImg}
                updateImage={(val) => setAttributes({ leftImg: val })}
              />
            )}
          </div>

          <div className="center">
            <RichText
              tagName="h6"
              className="top-header"
              value={contextHeader}
              onChange={(val) => setAttributes({ contextHeader: val })}
              placeholder={"HOST"}
            />
            <RichText
              tagName={hLevel}
              className="rbt-title"
              value={title}
              onChange={(val) => setAttributes({ title: val })}
              placeholder={"CTA Title"}
            />
            <RichText
              tagName="p"
              className="rbt-subtitle"
              value={subtitle}
              onChange={(val) => setAttributes({ subtitle: val })}
              placeholder={
                "Lorem ipsum dolor sit amet, consectetur adipiscing elit."
              }
            />
            <div className="rbt-calltoaction">
              <InnerBlocks />
            </div>
          </div>
          <div className="right">
            {(variant === "img-left-right" || variant === "img-right") && (
              <MediaUploaderGeneric
                photo={rightImg}
                updateImage={(val) => setAttributes({ rightImg: val })}
              />
            )}
          </div>
        </div>
      </div>,
    ];
  },
  save({ attributes }) {
    const {
      title,
      subtitle,
      bgColor,
      textColor,
      analyticsTag,
      bypassCrop,
      contextHeader,
      hLevel,
      variant,
      leftImg,
      rightImg,
    } = attributes;
    const styleL = { backgroundColor: bgColor ? bgColor : "transparent" };
    const styleR = { color: textColor ? textColor : "inherit" };
    const blockStyle = { ...styleL, ...styleR };
    const generateSourceSet = (media, desiredWidth) => {
      const sizes = media.mediaSizes;
      const srcset = [];

      for (const size in sizes) {
        if (sizes.hasOwnProperty(size) && size.includes("square")) {
          const width = sizes[size].width;
          if (width >= desiredWidth) {
            srcset.push(`${sizes[size].url} ${width}w`);
          }
        }
      }
      return srcset.length ? srcset.join(", ") : "";
    };
    return (
      <div className={"rbt-calltoaction"} style={blockStyle}>
        <div className={`layout ${variant}`}>
          <div className="left">
            {(variant === "img-left-right" || variant === "img-left") && (
              <img
                src={leftImg?.mediaUrl}
                srcSet={bypassCrop ? "" : generateSourceSet(leftImg, 500)}
                alt={title + " left image"}
                loading="lazy"
              />
            )}
          </div>
          <div className="center">
            <RichText.Content
              tagName="h6"
              value={contextHeader}
              className="top-header"
            />

            <RichText.Content
              tagName={hLevel ? hLevel : "h2"}
              className="rbt-title"
              value={title}
            />
            <div className="rbt-calltoaction-text">
              <RichText.Content tagName="h4" value={subtitle} />
              <div className="rbt-calltoaction">
                <InnerBlocks.Content />
              </div>
            </div>
          </div>
          <div className="right">
            {(variant === "img-left-right" || variant === "img-right") && (
              <img
                src={rightImg?.mediaUrl}
                srcSet={bypassCrop ? "" : generateSourceSet(rightImg, 500)}
                alt={title + " right image"}
              />
            )}
          </div>
        </div>
      </div>
    );
  },
};
registerBlockType("rbt/calltoaction", calltoaction);
export default calltoaction;
